import type { ServiceRequest } from "Models";
import type { UseDataInfo } from "QueryTypes";
import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import type { QueryState } from "redux-query";
import { useMemo } from "react";
import { type RunMutation, useMutation, useRequest } from "redux-query-react";
import type { EntitiesState, RootState } from "StoreTypes";
import {
  facilityServiceRequestsQuery,
  makeDismissAllServiceRequestsMutation,
  makeDismissServiceRequestMutation,
} from "./serviceRequestQueries";

// Selector factory to create memoized selectors for different facilityIds
const createServiceRequestsSelector = (facilityId: string) =>
  createSelector(
    [
      (state: RootState) =>
        state.entities.serviceRequestsByFacilityId?.[facilityId],
    ],
    (serviceRequests) => {
      if (!serviceRequests) return [];
      return Object.values(serviceRequests).sort(
        (a: ServiceRequest, b: ServiceRequest) =>
          new Date(b.createdDate).valueOf() - new Date(a.createdDate).valueOf(),
      );
    },
  );

export const useServiceRequestsByFacilityId = (
  facilityId: string,
): UseDataInfo<ServiceRequest[]> => {
  const safeQuery = useMemo(() => {
    if (!facilityId?.trim()) {
      console.log("facilityId is falsy or empty");
      return null;
    }
    return facilityServiceRequestsQuery(facilityId);
  }, [facilityId]);

  const [queryState, refeshFacilityServiceRequests] = useRequest(
    safeQuery ?? undefined, //facilityServiceRequestsQuery(facilityId)
  );

  const facilityServiceRequests = useSelector(
    createServiceRequestsSelector(facilityId),
  );

  return [facilityServiceRequests, queryState, refeshFacilityServiceRequests];
};

export const useDismissServiceRequest = (
  serviceRequestId: string,
): [QueryState, RunMutation<EntitiesState>] => {
  const [dismissServiceRequestQueryState, dismissServiceRequest] = useMutation(
    () => makeDismissServiceRequestMutation(serviceRequestId),
  );

  return [dismissServiceRequestQueryState, dismissServiceRequest];
};

export const useDismissAllServiceRequests = (
  facilityId: string,
): [QueryState, RunMutation<EntitiesState>] => {
  const [dismissAllServiceRequestQueryState, dismissAllServiceRequest] =
    useMutation(() => makeDismissAllServiceRequestsMutation(facilityId));

  return [dismissAllServiceRequestQueryState, dismissAllServiceRequest];
};
